import { useCallback, useEffect, useId, useRef, useState } from 'react'

import { cn } from '@/lib/utils'

import { FxIcon } from './FxIcon'

/**
 * The upload overlay: a `radius-xl` white dialog over the dimmed page, with
 * the dashed `primary` dropzone on `primary-subtle`.
 *
 * The page behind is dimmed by `shadow-scrim` — the design system's 100vmax
 * spread shadow, not a tinted element. A shadow cannot swallow a click, so
 * the fixed wrapper around the dialog is what closes it when the page behind
 * is clicked.
 *
 * Beyond the design system's contract this takes `children` and `footer`.
 * The target design's version of this dialog
 * (`.work/zieldesign/project/UploadDialog.dc.html`) carries a file list with
 * per-file states and a credit line next to two buttons; hard-coding that
 * body would make the component unusable for the template upload, which has
 * neither. The design system's own preview is what you get by passing
 * neither.
 */

export interface FxUploadDialogProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  title?: string
  subtitle?: React.ReactNode
  /** Enables the submit button. The caller owns the file handling. */
  ready?: boolean
  onClose?: () => void
  onSubmit?: () => void
  submitLabel?: string
  accept?: string
  multiple?: boolean
  onFilesSelected?: (files: FileList) => void
  /** The accepted formats and the size limit, under the dropzone. */
  formats?: string
  maxSizeLabel?: string
  /** Between the dropzone's meta line and the footer: the file list. */
  children?: React.ReactNode
  /** Replaces the single full-width submit button. */
  footer?: React.ReactNode
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

export function FxUploadDialog({
  title = 'Leeres Musterformular hochladen',
  subtitle,
  ready = false,
  onClose,
  onSubmit,
  submitLabel = 'Hinzufügen & Bearbeiten',
  accept = '.jpg,.jpeg,.png,.pdf',
  multiple = false,
  onFilesSelected,
  formats = 'Unterstützte Formate: jpg, png, pdf',
  maxSizeLabel = 'Maximalgröße: 50 MB',
  children,
  footer,
  className,
  ...props
}: FxUploadDialogProps) {
  const titleId = useId()
  const dialog = useRef<HTMLDivElement>(null)
  const [over, setOver] = useState(false)

  // Escape closes, and Tab stays inside: a modal dialog that leaks focus to
  // the page it is covering is unusable with a keyboard or a screen reader.
  useEffect(() => {
    const node = dialog.current
    if (!node) return
    const previous = document.activeElement as HTMLElement | null
    node.querySelector<HTMLElement>(FOCUSABLE)?.focus()

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onClose?.()
        return
      }
      if (e.key !== 'Tab') return
      const items = Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE))
      if (items.length === 0) return
      const first = items[0]
      const last = items[items.length - 1]
      if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      } else if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      }
    }

    node.addEventListener('keydown', onKeyDown)
    return () => {
      node.removeEventListener('keydown', onKeyDown)
      previous?.focus?.()
    }
  }, [onClose])

  const take = useCallback(
    (files: FileList | null) => {
      if (files && files.length > 0) onFilesSelected?.(files)
    },
    [onFilesSelected],
  )

  return (
    <div
      className="fixed inset-0 z-50 flex justify-center overflow-y-auto p-4 sm:p-8"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.()
      }}
    >
      {/* The design system dims the page with a 100vmax spread shadow rather
          than a tinted element, so there is no scrim colour to get wrong. It
          sits on a zero-sized box of its own because a box-shadow utility
          cannot be combined with the dialog's own `shadow-overlay`. */}
      <div aria-hidden className="pointer-events-none fixed left-1/2 top-1/2 h-0 w-0 shadow-scrim" />

      <div
        ref={dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={cn(
          'relative my-auto flex h-fit w-[780px] max-w-full flex-col gap-4 rounded-xl bg-surface p-10 shadow-overlay',
          className,
        )}
        {...props}
      >
        <button
          type="button"
          aria-label="Schließen"
          onClick={onClose}
          className="absolute right-10 top-10 flex rounded-sm text-text"
        >
          <FxIcon name="close" size={30} />
        </button>

        <h2 id={titleId} className="m-0 px-10 text-center text-dialog-title">
          {title}
        </h2>
        <p className="mb-4 mt-0 text-center">
          {subtitle ?? (
            <>
              Lade ein Bild eines <b>unausgefüllten Formulars</b> hoch.
            </>
          )}
        </p>

        <label
          className={cn(
            'relative flex min-h-[240px] cursor-pointer flex-col items-center justify-center gap-2 rounded-sm border-[1.5px] border-dashed border-primary p-8 text-center',
            over ? 'bg-primary-tonal' : 'bg-primary-subtle',
          )}
          onDragOver={(e) => {
            e.preventDefault()
            setOver(true)
          }}
          onDragLeave={() => setOver(false)}
          onDrop={(e) => {
            e.preventDefault()
            setOver(false)
            take(e.dataTransfer?.files ?? null)
          }}
        >
          <input
            type="file"
            aria-label="Dateien auswählen"
            accept={accept}
            multiple={multiple}
            onChange={(e) => take(e.target.files)}
            className="absolute inset-0 cursor-pointer opacity-0"
          />
          <FxIcon name="assignment_add" size={96} className="text-primary-strong" />
          <span className="mt-2 text-tile-title">Drag &amp; Drop zum Hochladen</span>
          <span className="underline">oder durchsuche deine Dateien</span>
        </label>

        <div className="flex flex-wrap justify-between gap-4 text-small">
          <span>{formats}</span>
          <span>{maxSizeLabel}</span>
        </div>

        {children}

        {footer ?? (
          <button
            type="button"
            disabled={!ready}
            onClick={onSubmit}
            className={cn(
              'flex h-control w-full items-center justify-center rounded-md text-body-strong shadow-raised',
              ready
                ? 'bg-primary-strong text-on-primary hover:bg-primary-pressed'
                : 'bg-fill text-text-on-disabled',
            )}
          >
            {submitLabel}
          </button>
        )}
      </div>
    </div>
  )
}
